import type { components as CmsComponents } from '@lifeserver/models/cms';
import type { components as GiftComponents } from '@lifeserver/models/gift';

import { isValidDate } from '../utils/dates';

type Gift = GiftComponents['schemas']['FullGiftDTO'];
type JiraSubtask = CmsComponents['schemas']['JiraSubtask'];

/** A scheduled gift is linked to a plan only by its persisted Jira issue key. */
export const isGiftLinkedToSubtask = (gift: Gift, subtask: JiraSubtask) =>
    gift.jiraIssueKey === subtask.key;

/** Finds the scheduled gift linked to a planned subtask, if any. */
export const findGiftForSubtask = <G extends Gift>(
    gifts: readonly G[] | undefined,
    subtask: JiraSubtask,
): G | null =>
    (gifts ?? []).find((gift) => isGiftLinkedToSubtask(gift, subtask)) ?? null;

/** Finds the planned subtask linked to a scheduled gift, if any. */
export const findSubtaskForGift = (
    subtasks: readonly JiraSubtask[] | undefined,
    gift: Gift,
): JiraSubtask | null =>
    (subtasks ?? []).find((subtask) => isGiftLinkedToSubtask(gift, subtask)) ??
    null;

/** A plan can be scheduled once it has valid dates and both game elements. */
export const canScheduleSubtask = (subtask: JiraSubtask) =>
    isValidDate(subtask.startTime) &&
    isValidDate(subtask.endTime) &&
    !!subtask.wrappingGameElementId &&
    !!subtask.contentGameElementId;

/** Primary tile image selection for a planned gift: content first, then wrapper. */
export const plannedGiftTileBigId = (subtask: JiraSubtask) =>
    subtask.contentGameElementId || subtask.wrappingGameElementId || undefined;
